import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import type { Dayjs } from "dayjs";
import isEmpty from "lodash/isEmpty";
import maxBy from "lodash/maxBy";
import sum from "lodash/sum";

import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { type DeviceType, devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import type { ICategory } from "app/common/services/categoryService.types";
import { chosenSites } from "app/common/services/chosenSites";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import { orderedTrafficSources } from "app/Shared/utils";

import type { ISiteLegendItem } from "../ChannelAnalysisChart/ChannelAnalysisChartContainer";

import { getChartConfigCompareMode } from "./AllTrafficChannelsOverviewBarChartUtils";
import { getPointY, sumObject } from "./AllTrafficChannelsOverviewBarChartShared";
import { ChannelsOverviewBarChart } from "./ChannelsOverviewBarChart";
import { ChannelsOverviewBarChartCategoryBenchmark } from "./ChannelsOverviewBarChartCategoryBenchmark";
import { translateSourceText } from "./ChannelsOverviewBarChartConfig";
import type { DataFormat } from "./ChannelsOverviewBarChartContainerFormat";
import { dataModeMap } from "./ChannelsOverviewBarChartContainerFormat";
import ChannelsOverviewBarChartLegend from "./components/channels-overview-bar-chart-legend/ChannelsOverviewBarChartLegend";
import { ChartContainer } from "./StyledComponents";
import type { IndustryDataByDeviceType, TrafficDataByDeviceType } from "./types";
import { allTrackers } from "app/services/track/track";

interface ILegendItem extends Partial<ISiteLegendItem> {
    isCategory?: boolean;
    data: number;
    visible: boolean;
}

interface IAllTrafficChannelsOverviewBarChartCompareMode {
    category: ICategory;
    data: {
        trafficData: TrafficDataByDeviceType;
        industryData: IndustryDataByDeviceType;
    };
    dataFormat: DataFormat;
    fromDate: Dayjs;
    onCategorySelected: (category: ICategory | null) => void;
}

const formatNumber = minVisitsAbbrFilter();

const getWinner = (data: TrafficDataByDeviceType) => {
    return maxBy(Object.keys(data.Total), (domain) => sumObject(data.Total[domain]));
};

export const AllTrafficChannelsOverviewBarChartBenchmark = ({
    category,
    data,
    dataFormat,
    fromDate,
    onCategorySelected,
}: IAllTrafficChannelsOverviewBarChartCompareMode) => {
    const { params } = useCurrentStateAndParams<{ key: string; duration: string }>();
    const { duration } = params;
    const isCompare = isCompareMode();
    const getLegends = () => {
        const initLegends: ILegendItem[] = chosenSites.sitelistForLegend().map((item) => ({
            name: item.name,
            color: item.color,
            visible: true,
            data: null,
            isWinner: null,
        }));
        if (data?.industryData) {
            initLegends.push({
                data: formatNumber(sum(Object.values(data.industryData.Total))),
                visible: true,
                name: category.text,
                color: colorsPalettes.midnight[50],
                isCategory: true,
            });
        }
        return initLegends;
    };
    const [legends, setLegends] = useState<ILegendItem[]>(getLegends);

    const handleLegendsChange = (changedLegend: ILegendItem) => {
        setLegends((prevState) =>
            prevState.map((prevLegend) =>
                prevLegend.name === changedLegend.name ? { ...prevLegend, visible: !prevLegend.visible } : prevLegend,
            ),
        );

        allTrackers.dispatchTrackEvent(
            "Graph Filter",
            "click",
            `Channel Overview graph/MMX/All Traffic/${changedLegend.name || ""}/Legend/${changedLegend.visible ? "add" : "remove"}`,
        );
    };

    useEffect(() => {
        if (data?.trafficData) {
            const winner = getWinner(data.trafficData);

            setLegends((prevState) =>
                prevState.map((legendItem) => {
                    if (legendItem.isCategory) {
                        return legendItem;
                    }

                    const totalVisits = sumObject(data.trafficData.Total[legendItem.name]);

                    return {
                        ...legendItem,
                        data: totalVisits ? formatNumber(totalVisits) : "N/A",
                        isWinner: legendItem.name === winner,
                    };
                }),
            );
        }
    }, [data]);

    const configCompareMode = useMemo(
        () =>
            data.trafficData &&
            getChartConfigCompareMode({
                data: data.trafficData,
                industryData: data.industryData,
                legends,
                category,
                dataFormat,
                fromDate,
            }),
        [data, legends, category, dataFormat, fromDate],
    );

    const series = useMemo(() => {
        if (isEmpty(data?.trafficData)) {
            return [];
        }

        // Build the union of sources present across all sites, in priority order.
        // Must match the xAxis category set, which `getCategories` derives from
        // `trafficData.Total` only — including `industryData` here would add a
        // point the xAxis doesn't have and shift later values one column over.
        const sourcesPresentInData = new Set<string>();
        Object.values(data.trafficData.Total ?? {}).forEach((domainSources: Record<string, number>) => {
            Object.keys(domainSources ?? {}).forEach((s) => sourcesPresentInData.add(s));
        });
        const alignedSources = orderedTrafficSources.filter((source) => sourcesPresentInData.has(source));

        const siteDataPoints = legends
            .map((legendItem) => {
                if (legendItem.visible && !legendItem.isCategory) {
                    const domainName = legendItem.name;
                    const domainData = data.trafficData.Total[domainName];
                    const totalVisits = sumObject(domainData);
                    const getFullDataForWebsite = (device: DeviceType) => {
                        return alignedSources.map((sourceName) => {
                            const value = data.trafficData?.[device]?.[domainName]?.[sourceName];
                            const hasValue = value !== undefined && value !== null;
                            return {
                                key: translateSourceText(sourceName),
                                y: hasValue ? getPointY(value, totalVisits, dataFormat) : null,
                                color: isCompare
                                    ? chosenSites.getSiteColor(domainName)
                                    : CHART_COLORS.trafficSourcesColorsBySourceMMX[sourceName],
                            };
                        });
                    };
                    return {
                        name: domainName,
                        desktop: getFullDataForWebsite(devicesTypes.DESKTOP),
                        mobile: getFullDataForWebsite(devicesTypes.MOBILE),
                        data: getFullDataForWebsite(devicesTypes.TOTAL),
                    };
                }

                return undefined;
            })
            .filter((datum) => datum !== undefined);

        const industrySeriesEnabled = legends.find((legend) => legend.isCategory)?.visible ?? false;

        if (!isEmpty(data.industryData) && industrySeriesEnabled) {
            const industryTotalVisits = sumObject(data.industryData.Total);
            const getFullDataForIndustry = (device: DeviceType) => {
                return alignedSources.map((sourceName) => {
                    const value = data.industryData?.[device]?.[sourceName];
                    const hasValue = value !== undefined && value !== null;
                    return {
                        key: translateSourceText(sourceName),
                        y: hasValue ? getPointY(value, industryTotalVisits, dataFormat) : null,
                        color: colorsPalettes.midnight[50],
                    };
                });
            };

            const industryDataPoint = {
                name: translate("workspace.marketing.arena.channels.overview.benchmark", { category: category.text }),
                id: category.id,
                grouping: false,
                zIndex: -1,
                pointWidth: null,
                pointRange: 1.5,
                pointPadding: 0.15,
                maxPointWidth: 70,
                borderWidth: 0,
                dataLabels: { enabled: false },
                states: { hover: { enabled: false } },
                desktop: getFullDataForIndustry(devicesTypes.DESKTOP),
                mobile: getFullDataForIndustry(devicesTypes.MOBILE),
                data: getFullDataForIndustry(devicesTypes.TOTAL),
            };

            siteDataPoints.push(industryDataPoint);
        }

        return siteDataPoints;
    }, [category, data, dataFormat, isCompare, legends]);

    return (
        <ChartContainer>
            <Box flexGrow="1">
                <ChannelsOverviewBarChart
                    key={String(series.length)}
                    data={series}
                    config={configCompareMode}
                    dataAutomation="ChannelsOverviewBarChartAllTraffic"
                />
            </Box>
            {legends && legends.length > 0 ? (
                <Flex
                    flexDirection="column"
                    gap="16px"
                    margin="24px 24px 24px 21px"
                >
                    <ChannelsOverviewBarChartCategoryBenchmark
                        isUnbound={false}
                        categoryParam={category.id}
                        dataMode={dataModeMap[dataFormat]}
                        durationParam={duration}
                        onCategorySelected={onCategorySelected}
                    />
                    <ChannelsOverviewBarChartLegend
                        legendItems={legends}
                        onLegendsChange={handleLegendsChange}
                        titles={[
                            translate("common.domain"),
                            translate("mmx.channelsoverview.channels.legend.values.title"),
                        ]}
                    />
                </Flex>
            ) : null}
        </ChartContainer>
    );
};
